import React, { useState, useEffect, useCallback } from 'react';
import { LLMAnalysisEnhancement, UserRequest, Account } from '../types';
import { Copy, ThumbsUp, ThumbsDown, AlertTriangle, Heart, Activity, Stethoscope, Calendar, User, Thermometer, Scale, Shield, ChevronDown, ChevronUp, Settings, Edit2 } from 'lucide-react';
import { api } from '../services/api';
import { usePlatformAuth } from '../contexts/AuthContext';
import { CombinedSoapNoteEditor } from './CombinedSoapNoteEditor';

interface AnnualWellWomanVisitNotesProps {
  llmAnalysisEnhancement: LLMAnalysisEnhancement | null;
  userRequest?: UserRequest | null;
  account?: Account | null;
}

export const AnnualWellWomanVisitNotesComponent: React.FC<AnnualWellWomanVisitNotesProps> = ({
  llmAnalysisEnhancement,
  userRequest,
  account: accountProp
}) => {
  const auth = usePlatformAuth();
  const [account, setAccount] = useState<Account | null>(accountProp || null);

  // Load account if not provided as prop
  useEffect(() => {
    if (accountProp) {
      setAccount(accountProp);
      return;
    }

    if (!auth.isAuthenticated) return;

    let cancelled = false;

    async function loadAccount() {
      try {
        const connectedId = import.meta.env.VITE_STRIPE_CONNECTED_ACCOUNT_ID;
        if (!connectedId) return;
        
        const idToken = await auth.getIdToken();
        if (!idToken) return;
        
        const acct = await api.getAccount(connectedId, idToken);
        if (!cancelled) {
          setAccount(acct);
        }
      } catch (err) {
        console.error('Failed to load account for placeholder replacement:', err);
      }
    }

    loadAccount();
    
    return () => {
      cancelled = true;
    };
  }, [auth.isAuthenticated, accountProp]);

  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    'soap': true,
    'patient-summary': true,
    'recommendations': true,
    'well-woman-extension': true,
    'key-concepts': true,
    'structured-extraction': true
  });

  const [copiedStates, setCopiedStates] = useState<Record<string, boolean>>({});
  const [feedbackStates, setFeedbackStates] = useState<Record<string, boolean | null>>({});
  const [isSendModalOpen, setIsSendModalOpen] = useState(false);
  const [emailAddress, setEmailAddress] = useState<string>('');
  const [saveToPatient, setSaveToPatient] = useState<boolean>(true);
  const [sending, setSending] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [editedSummary, setEditedSummary] = useState<string>('');
  
  // Combined SOAP note editing state
  const [editedCombinedSoapNote, setEditedCombinedSoapNote] = useState<string | undefined>(
    llmAnalysisEnhancement?.editedCombinedSoapNote
  );
  const [isSavingSoapNote, setIsSavingSoapNote] = useState(false);

  // Helper function to replace placeholders in patient summary
  const replacePlaceholders = (text: string): string => {
    if (!text) return text;
    let result = text;
    const clinicianName = account?.settings?.['Communication.ClinicianName'] || '';
    
    // Replace all variations of clinician name placeholders
    if (clinicianName) {
      result = result.replace(/\[Clinician's Name and Credentials\]/gi, clinicianName);
      result = result.replace(/\[Clinician Name and Credentials\]/gi, clinicianName);
      result = result.replace(/\[Clinician's Name\]/gi, clinicianName);
      result = result.replace(/\[Clinician Name\]/gi, clinicianName);
      result = result.replace(/\[Clinician\]/gi, clinicianName);
    }
    
    // Replace patient name placeholder with actual patient name if available
    if (userRequest?.patientName) {
      result = result.replace(/\[Patient Name\]/gi, userRequest.patientName);
      result = result.replace(/\[Patient's Name\]/gi, userRequest.patientName);
      result = result.replace(/\[Patient\]/gi, userRequest.patientName);
    }
    
    return result;
  };


  const toggleSection = (sectionName: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionName]: !prev[sectionName]
    }));
  };

  const copyToClipboard = async (text: string, buttonKey: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedStates(prev => ({ ...prev, [buttonKey]: true }));
      setTimeout(() => {
        setCopiedStates(prev => ({ ...prev, [buttonKey]: false }));
      }, 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const handleFeedback = (sectionName: string, isPositive: boolean) => {
    setFeedbackStates(prev => ({
      ...prev,
      [sectionName]: isPositive
    }));
    console.log(`Feedback for section ${sectionName}: ${isPositive ? 'positive' : 'negative'}`);
  };

  // Handle combined SOAP note edit save
  const handleCombinedSoapNoteSave = useCallback(async (editedCombinedNote: string) => {
    if (!userRequest?.id) {
      console.warn('No user request ID available for saving SOAP note edit');
      setEditedCombinedSoapNote(editedCombinedNote);
      return;
    }

    setIsSavingSoapNote(true);
    try {
      // Call API to save the edited combined SOAP note
      await api.saveCombinedSoapNoteEdit(userRequest.id, editedCombinedNote);

      // Update local state with the saved note
      setEditedCombinedSoapNote(editedCombinedNote);

      console.log('Combined SOAP note edit saved:', { editedCombinedNote });
    } catch (error) {
      console.error('Failed to save SOAP note edit:', error);
      alert('Failed to save SOAP note changes. Please try again.');
    } finally {
      setIsSavingSoapNote(false);
    }
  }, [userRequest?.id]);

  const handleOpenSettings = () => {
    window.dispatchEvent(new CustomEvent('openSettings'));
  };

  const openSendModal = async () => {
    try {
      if (userRequest?.patientId) {
        const patient = await api.getPatient(userRequest.patientId);
        setEmailAddress(patient.email || '');
      }
      // Initialize editedSummary with replaced placeholders if not already set
      if (!editedSummary && wellWomanData?.patientSummary) {
        setEditedSummary(replacePlaceholders(wellWomanData.patientSummary));
      } else if (!editedSummary) {
        setEditedSummary(wellWomanData?.patientSummary || '');
      }
    } catch (e) {
      console.warn('Failed to load patient for send modal', e);
    } finally {
      setIsSendModalOpen(true);
    }
  };

  const handleCloseSendModal = () => {
    setIsSendModalOpen(false);
    setShowSuccess(false);
  };

  const submitSend = async (summary: string) => {
    if (!userRequest?.patientId) {
      alert('Please associate a patient with this request before sending. You can add a patient from the request details.');
      return;
    }
    if (!summary?.trim()) {
      alert('No patient summary available to send.');
      return;
    }
    if (!emailAddress?.trim()) {
      alert('Please enter a valid email address.');
      return;
    }
    try {
      setSending(true);
      await api.sendPatientSummary(userRequest.patientId, {
        requestId: userRequest.id,
        channel: 'email',
        destination: emailAddress,
        saveToPatient,
        summary
      });
      setShowSuccess(true);
      setTimeout(() => {
        setIsSendModalOpen(false);
        setShowSuccess(false);
      }, 500);
    } catch (e: any) {
      alert(`Failed to send: ${e?.message || 'Unknown error'}`);
    } finally {
      setSending(false);
    }
  };

  // Formatting functions for copying human-readable text
  const formatkeyConcepts = (concepts: any[]): string => {
    let text = 'KEY CONCEPTS\n\n';
    concepts.forEach((concept, index) => {
      text += `${index + 1}. ${concept.label}\n`;
      text += `   Code: ${concept.code} (${concept.codeSystem})\n`;
      text += `   Justification: ${concept.justification}\n\n`;
    });
    return text;
  };

  const formatRecommendations = (recommendations: any[]): string => {
    let text = 'RECOMMENDATIONS\n\n';
    recommendations.forEach((rec, index) => {
      text += `${index + 1}. ${rec.action}\n`;
      text += `   Category: ${rec.category}\n`;
      text += `   Confidence: ${rec.confidence}\n`;
      text += `   Rationale: ${rec.rationale}\n\n`;
    });
    return text;
  };

  const formatstructuredExtraction = (extraction: any): string => {
    let text = 'STRUCTURED EXTRACTION\n\n';

    // DemographicsAndIdentity
    if (extraction.demographicsAndIdentity) {
      text += 'DEMOGRAPHICS AND IDENTITY:\n';
      Object.entries(extraction.demographicsAndIdentity).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          const label = key.replace(/([A-Z])/g, ' $1').trim();
          text += `  ${label}: ${value}\n`;
        }
      });
      text += '\n';
    }

    // MenstrualHistory
    if (extraction.menstrualHistory) {
      text += 'MENSTRUAL HISTORY:\n';
      Object.entries(extraction.menstrualHistory).forEach(([key, value]) => {
        if (value !== null && value !== undefined && key !== 'SymptomList') {
          const label = key.replace(/([A-Z])/g, ' $1').trim();
          text += `  ${label}: ${value}\n`;
        }
      });
      text += '\n';
    }

    // SexualHistory
    if (extraction.sexualHistory) {
      text += 'SEXUAL HISTORY:\n';
      Object.entries(extraction.sexualHistory).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          const label = key.replace(/([A-Z])/g, ' $1').trim();
          text += `  ${label}: ${value}\n`;
        }
      });
      text += '\n';
    }

    // ContraceptionAndPregnancyIntent
    if (extraction.contraceptionAndPregnancyIntent) {
      text += 'CONTRACEPTION AND PREGNANCY INTENT:\n';
      Object.entries(extraction.contraceptionAndPregnancyIntent).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          const label = key.replace(/([A-Z])/g, ' $1').trim();
          text += `  ${label}: ${value}\n`;
        }
      });
      text += '\n';
    }

    // ScreeningAndPreventiveCare
    if (extraction.screeningAndPreventiveCare) {
      text += 'SCREENING AND PREVENTIVE CARE:\n';
      Object.entries(extraction.screeningAndPreventiveCare).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          const label = key.replace(/([A-Z])/g, ' $1').trim();
          text += `  ${label}: ${value}\n`;
        }
      });
      text += '\n';
    }

    // VitalsAndBodyMetrics
    if (extraction.vitalsAndBodyMetrics) {
      text += 'VITALS AND BODY METRICS:\n';
      Object.entries(extraction.vitalsAndBodyMetrics).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          const label = key.replace(/([A-Z])/g, ' $1').trim();
          text += `  ${label}: ${value}\n`;
        }
      });
      text += '\n';
    }

    // SymptomsAndConcerns
    if (extraction.symptomsAndConcerns) {
      text += 'SYMPTOMS AND CONCERNS:\n';
      if (extraction.symptomsAndConcerns.chiefComplaint) {
        text += `  Chief Complaint: ${extraction.symptomsAndConcerns.chiefComplaint}\n`;
      }
      if (extraction.symptomsAndConcerns.symptomList && extraction.symptomsAndConcerns.symptomList.length > 0) {
        extraction.symptomsAndConcerns.symptomList.forEach((symptom: any, index: number) => {
          text += `  ${index + 1}. ${symptom.name}: ${symptom.present ? 'Present' : 'Absent'}\n`;
          if (symptom.severity) text += `     Severity: ${symptom.severity}\n`;
          if (symptom.onsetOrPattern) text += `     Onset/Pattern: ${symptom.onsetOrPattern}\n`;
        });
      }
      text += '\n';
    }

    return text;
  };

  const renderSectionActions = (sectionName: string, content: string) => {
    const copyButtonKey = `${sectionName}-copy`;
    const isCopied = copiedStates[copyButtonKey];
    const feedback = feedbackStates[sectionName];

    return (
      <div className="flex items-center space-x-1">
        <button
          className="inline-flex items-center px-2 py-1 text-xs font-medium rounded border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          onClick={() => copyToClipboard(content, copyButtonKey)}
          title="Copy to clipboard"
        >
          <Copy className="w-3 h-3 mr-1" />
          {isCopied ? 'Copied!' : 'Copy'}
        </button>
        <button
          className={`inline-flex items-center p-1 rounded transition-colors duration-200 focus:outline-none focus:ring-1 focus:ring-gray-300 ${
            feedback === true 
              ? 'text-green-600 bg-green-50' 
              : 'text-gray-500 hover:text-green-600 hover:bg-green-50'
          }`}
          onClick={() => handleFeedback(sectionName, true)}
          title="Good"
        >
          <ThumbsUp className="w-3 h-3" />
        </button>
        <button
          className={`inline-flex items-center p-1 rounded transition-colors duration-200 focus:outline-none focus:ring-1 focus:ring-gray-300 ${
            feedback === false 
              ? 'text-red-600 bg-red-50' 
              : 'text-gray-500 hover:text-red-600 hover:bg-red-50'
          }`}
          onClick={() => handleFeedback(sectionName, false)}
          title="Needs improvement"
        >
          <ThumbsDown className="w-3 h-3" />
        </button>
      </div>
    );
  };

  const renderCodedConcept = (concept: any, index: number) => (
    <div key={index} className="card bg-base-100 shadow-sm border border-base-300">
      <div className="card-body p-4">
        <div className="flex items-center gap-2 mb-2">
          <span className="badge badge-primary badge-sm">{concept.codeSystem}</span>
          <span className="badge badge-outline badge-sm font-mono">{concept.code}</span>
        </div>
        <h4 className="card-title text-base">{concept.label}</h4>
        <p className="text-sm text-base-content/80">{concept.justification}</p>
      </div>
    </div>
  );

  const renderRecommendation = (recommendation: any, index: number) => (
    <div key={index} className="card bg-base-100 shadow-sm border border-base-300">
      <div className="card-body p-4">
        <div className="flex items-center gap-2 mb-2">
          <span className="badge badge-secondary badge-sm">{recommendation.category}</span>
          <span className="badge badge-outline badge-sm">{recommendation.confidence}</span>
        </div>
        <h4 className="card-title text-base">{recommendation.action}</h4>
        <p className="text-sm text-base-content/80">{recommendation.rationale}</p>
      </div>
    </div>
  );

  if (!llmAnalysisEnhancement?.wellWomanOutput) {
    return (
      <div className="text-center py-8">
        <div className="mx-auto flex items-center justify-center h-12 w-12 bg-orange-100 rounded-full mb-4">
          <AlertTriangle className="h-6 w-6 text-orange-600" />
        </div>
        <div className="max-w-md mx-auto">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Annual Well Woman Visit Analysis Available</h3>
          <p className="text-gray-600 mb-4">
            No annual well woman visit analysis was generated for this request.
          </p>
        </div>
      </div>
    );
  }

  const wellWomanData = llmAnalysisEnhancement.wellWomanOutput;

  // Initialize editedSummary with replaced placeholders when account, patient, or data changes
  // This useEffect must be after wellWomanData is defined
  const patientSummaryText = wellWomanData?.patientSummary || '';
  
  useEffect(() => {
    if (!patientSummaryText) return;
    
    const replaced = replacePlaceholders(patientSummaryText);
    // Always update if replacement changed something or if editedSummary is empty
    if (replaced !== patientSummaryText || !editedSummary) {
      setEditedSummary(replaced);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account?.settings?.['Communication.ClinicianName'], userRequest?.patientName, patientSummaryText]);

  return (
    <>
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-base-content">Annual Well Woman Visit</h3>
      </div>

      {/* Content */}
      <div className="space-y-4">
          {/* SOAP Note - using CombinedSoapNoteEditor */}
          {wellWomanData.structuredSoap && (
            <CombinedSoapNoteEditor
              originalNote={wellWomanData.structuredSoap}
              editedCombinedNote={editedCombinedSoapNote}
              isExpanded={expandedSections['soap']}
              onToggleExpand={() => toggleSection('soap')}
              onSave={handleCombinedSoapNoteSave}
              isSaving={isSavingSoapNote}
              onCopy={(text) => copyToClipboard(text, 'soap-copy')}
              isCopied={copiedStates['soap-copy']}
              onFeedback={(isPositive) => handleFeedback('soap', isPositive)}
              feedbackState={feedbackStates['soap']}
            />
          )}

          {/* Patient Summary */}
          {wellWomanData.patientSummary && (
            <div className="bg-base-100 border border-base-300 rounded-lg">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 border-b border-base-300">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => toggleSection('patient-summary')}
                    className="p-1 hover:bg-base-200 rounded transition-colors"
                    title={expandedSections['patient-summary'] ? 'Collapse' : 'Expand'}
                  >
                    {expandedSections['patient-summary'] ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                  </button>
                  <span className="text-lg font-medium">Patient Summary</span>
                </div>
                <div className="flex items-center gap-2 flex-wrap self-end sm:self-auto">
                  <button
                    onClick={handleOpenSettings}
                    className="btn btn-ghost btn-sm btn-circle"
                    title="Open settings to configure clinician name"
                  >
                    <Settings className="w-4 h-4" />
                  </button>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={openSendModal}
                    title="Edit and send summary to patient"
                  >
                    <Edit2 className="w-4 h-4 mr-1" />
                    Edit & Send
                  </button>
                  {renderSectionActions('patient-summary', wellWomanData.patientSummary)}
                </div>
              </div>
              {expandedSections['patient-summary'] && (
                <div className="p-4">
                  <p className="text-gray-800 leading-relaxed whitespace-pre-line">
                    {editedSummary || replacePlaceholders(wellWomanData.patientSummary)}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Recommendations */}
          {wellWomanData.recommendations && wellWomanData.recommendations.length > 0 && (
            <div className="bg-base-100 border border-base-300 rounded-lg">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 border-b border-base-300">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => toggleSection('recommendations')}
                    className="p-1 hover:bg-base-200 rounded transition-colors"
                    title={expandedSections['recommendations'] ? 'Collapse' : 'Expand'}
                  >
                    {expandedSections['recommendations'] ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                  </button>
                  <span className="text-lg font-medium">Recommendations</span>
                </div>
                <div className="self-end sm:self-auto">
                  {renderSectionActions('recommendations', formatRecommendations(wellWomanData.recommendations))}
                </div>
              </div>
              {expandedSections['recommendations'] && (
                <div className="p-4">
                  <div className="space-y-3">
                    {wellWomanData.recommendations.map((recommendation, index) => renderRecommendation(recommendation, index))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Well Woman Extension */}
          {wellWomanData.wellWomanExtension && (
            <div className="bg-base-100 border border-base-300 rounded-lg">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 border-b border-base-300">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => toggleSection('well-woman-extension')}
                    className="p-1 hover:bg-base-200 rounded transition-colors"
                    title={expandedSections['well-woman-extension'] ? 'Collapse' : 'Expand'}
                  >
                    {expandedSections['well-woman-extension'] ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                  </button>
                  <span className="text-lg font-medium">Physical Examination</span>
                </div>
                <div className="self-end sm:self-auto">
                  {renderSectionActions('well-woman-extension', JSON.stringify(wellWomanData.wellWomanExtension, null, 2))}
                </div>
              </div>
              {expandedSections['well-woman-extension'] && (
                <div className="p-4">
                  <div className="space-y-4">
                    {wellWomanData.wellWomanExtension.papHistory && (
                      <div className="card bg-base-100 shadow-sm border border-base-300">
                        <div className="card-body p-4">
                          <h4 className="text-base font-semibold text-gray-900 mb-2">Pap History</h4>
                          <p className="text-gray-800">{wellWomanData.wellWomanExtension.papHistory}</p>
                        </div>
                      </div>
                    )}
                    {wellWomanData.wellWomanExtension.mostRecentPapResult && (
                      <div className="card bg-base-100 shadow-sm border border-base-300">
                        <div className="card-body p-4">
                          <h4 className="text-base font-semibold text-gray-900 mb-2">Most Recent Pap Result</h4>
                          <p className="text-gray-800">{wellWomanData.wellWomanExtension.mostRecentPapResult}</p>
                        </div>
                      </div>
                    )}
                    {wellWomanData.wellWomanExtension.breastExamFindings && (
                      <div className="card bg-base-100 shadow-sm border border-base-300">
                        <div className="card-body p-4">
                          <h4 className="text-base font-semibold text-gray-900 mb-2">Breast Exam Findings</h4>
                          <p className="text-gray-800">{wellWomanData.wellWomanExtension.breastExamFindings}</p>
                        </div>
                      </div>
                    )}
                    {wellWomanData.wellWomanExtension.pelvicExamFindings && (
                      <div className="card bg-base-100 shadow-sm border border-base-300">
                        <div className="card-body p-4">
                          <h4 className="text-base font-semibold text-gray-900 mb-2">Pelvic Exam Findings</h4>
                          <p className="text-gray-800">{wellWomanData.wellWomanExtension.pelvicExamFindings}</p>
                        </div>
                      </div>
                    )}
                    {wellWomanData.wellWomanExtension.lifestyleFactorsDiscussed && (
                      <div className="card bg-base-100 shadow-sm border border-base-300">
                        <div className="card-body p-4">
                          <h4 className="text-base font-semibold text-gray-900 mb-2">Lifestyle Factors Discussed</h4>
                          <p className="text-gray-800">{wellWomanData.wellWomanExtension.lifestyleFactorsDiscussed}</p>
                        </div>
                      </div>
                    )}
                    {wellWomanData.wellWomanExtension.counselingTopics && wellWomanData.wellWomanExtension.counselingTopics.length > 0 && (
                      <div className="card bg-base-100 shadow-sm border border-base-300">
                        <div className="card-body p-4">
                          <h4 className="text-base font-semibold text-gray-900 mb-2">Counseling Topics</h4>
                          <ul className="list-disc list-inside space-y-1">
                            {wellWomanData.wellWomanExtension.counselingTopics.map((topic, index) => (
                              <li key={index} className="text-gray-800">{topic}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    )}
                    {wellWomanData.wellWomanExtension.guidelineScreeningsDueOrSuggested && wellWomanData.wellWomanExtension.guidelineScreeningsDueOrSuggested.length > 0 && (
                      <div className="card bg-base-100 shadow-sm border border-base-300">
                        <div className="card-body p-4">
                          <h4 className="text-base font-semibold text-gray-900 mb-2">Guideline Screenings Due or Suggested</h4>
                          <div className="space-y-3">
                            {wellWomanData.wellWomanExtension.guidelineScreeningsDueOrSuggested.map((screening, index) => (
                              <div key={index} className="border border-base-300 rounded-lg p-3">
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="badge badge-primary badge-sm">{screening.basedOn}</span>
                                </div>
                                <h5 className="font-semibold text-base-content">{screening.service}</h5>
                                <p className="text-sm text-base-content/80 mt-1">{screening.reason}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Key Concepts */}
          {wellWomanData.keyConcepts && wellWomanData.keyConcepts.length > 0 && (
            <div className="bg-base-100 border border-base-300 rounded-lg">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 border-b border-base-300">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => toggleSection('key-concepts')}
                    className="p-1 hover:bg-base-200 rounded transition-colors"
                    title={expandedSections['key-concepts'] ? 'Collapse' : 'Expand'}
                  >
                    {expandedSections['key-concepts'] ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                  </button>
                  <span className="text-lg font-medium">Key Concepts</span>
                </div>
                <div className="self-end sm:self-auto">
                  {renderSectionActions('key-concepts', formatkeyConcepts(wellWomanData.keyConcepts))}
                </div>
              </div>
              {expandedSections['key-concepts'] && (
                <div className="p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {wellWomanData.keyConcepts.map((concept, index) => renderCodedConcept(concept, index))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Structured Extraction */}
          {wellWomanData.structuredExtraction && (
            <div className="bg-base-100 border border-base-300 rounded-lg">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 border-b border-base-300">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => toggleSection('structured-extraction')}
                    className="p-1 hover:bg-base-200 rounded transition-colors"
                    title={expandedSections['structured-extraction'] ? 'Collapse' : 'Expand'}
                  >
                    {expandedSections['structured-extraction'] ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                  </button>
                  <span className="text-lg font-medium">Clinical Data</span>
                </div>
                <div className="self-end sm:self-auto">
                  {renderSectionActions('structured-extraction', formatstructuredExtraction(wellWomanData.structuredExtraction))}
                </div>
              </div>
              {expandedSections['structured-extraction'] && (
                <div className="p-4">
                  <div className="space-y-6">
                  {wellWomanData.structuredExtraction.demographicsAndIdentity && (
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">Demographics and Identity</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {Object.entries(wellWomanData.structuredExtraction.demographicsAndIdentity).map(([key, value]) => (
                          value !== null && value !== undefined && (
                            <div key={key} className="card bg-base-100 shadow-sm border border-base-300">
                              <div className="card-body p-3">
                                <div className="flex items-center gap-2 mb-1">
                                  <User className="w-4 h-4 text-base-content/80" />
                                  <div className="text-xs text-base-content/80 uppercase tracking-wide font-semibold">{key.replace(/([A-Z])/g, ' $1').trim()}</div>
                                </div>
                                <div className="text-base-content font-medium">{String(value)}</div>
                              </div>
                            </div>
                          )
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {wellWomanData.structuredExtraction.menstrualHistory && (
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">Menstrual History</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {Object.entries(wellWomanData.structuredExtraction.menstrualHistory).map(([key, value]) => (
                          value !== null && value !== undefined && key !== 'SymptomList' && (
                            <div key={key} className="card bg-base-100 shadow-sm border border-base-300">
                              <div className="card-body p-3">
                                <div className="flex items-center gap-2 mb-1">
                                  <Calendar className="w-4 h-4 text-base-content/80" />
                                  <div className="text-xs text-base-content/80 uppercase tracking-wide font-semibold">{key.replace(/([A-Z])/g, ' $1').trim()}</div>
                                </div>
                                <div className="text-base-content font-medium">{String(value)}</div>
                              </div>
                            </div>
                          )
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {wellWomanData.structuredExtraction.sexualHistory && (
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">Sexual History</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {Object.entries(wellWomanData.structuredExtraction.sexualHistory).map(([key, value]) => (
                          value !== null && value !== undefined && (
                            <div key={key} className="card bg-base-100 shadow-sm border border-base-300">
                              <div className="card-body p-3">
                                <div className="flex items-center gap-2 mb-1">
                                  <Heart className="w-4 h-4 text-base-content/80" />
                                  <div className="text-xs text-base-content/80 uppercase tracking-wide font-semibold">{key.replace(/([A-Z])/g, ' $1').trim()}</div>
                                </div>
                                <div className="text-base-content font-medium">{String(value)}</div>
                              </div>
                            </div>
                          )
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {wellWomanData.structuredExtraction.contraceptionAndPregnancyIntent && (
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">Contraception and Pregnancy Intent</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {Object.entries(wellWomanData.structuredExtraction.contraceptionAndPregnancyIntent).map(([key, value]) => (
                          value !== null && value !== undefined && (
                            <div key={key} className="card bg-base-100 shadow-sm border border-base-300">
                              <div className="card-body p-3">
                                <div className="flex items-center gap-2 mb-1">
                                  <Shield className="w-4 h-4 text-base-content/80" />
                                  <div className="text-xs text-base-content/80 uppercase tracking-wide font-semibold">{key.replace(/([A-Z])/g, ' $1').trim()}</div>
                                </div>
                                <div className="text-base-content font-medium">{String(value)}</div>
                              </div>
                            </div>
                          )
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {wellWomanData.structuredExtraction.screeningAndPreventiveCare && (
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">Screening and Preventive Care</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {Object.entries(wellWomanData.structuredExtraction.screeningAndPreventiveCare).map(([key, value]) => (
                          value !== null && value !== undefined && (
                            <div key={key} className="card bg-base-100 shadow-sm border border-base-300">
                              <div className="card-body p-3">
                                <div className="flex items-center gap-2 mb-1">
                                  <Shield className="w-4 h-4 text-base-content/80" />
                                  <div className="text-xs text-base-content/80 uppercase tracking-wide font-semibold">{key.replace(/([A-Z])/g, ' $1').trim()}</div>
                                </div>
                                <div className="text-base-content font-medium">{String(value)}</div>
                              </div>
                            </div>
                          )
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {wellWomanData.structuredExtraction.vitalsAndBodyMetrics && (
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">Vitals and Body Metrics</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {Object.entries(wellWomanData.structuredExtraction.vitalsAndBodyMetrics).map(([key, value]) => (
                          value !== null && value !== undefined && (
                            <div key={key} className="card bg-base-100 shadow-sm border border-base-300">
                              <div className="card-body p-3">
                                <div className="flex items-center gap-2 mb-1">
                                  {key.toLowerCase().includes('temperature') ? <Thermometer className="w-4 h-4 text-base-content/80" /> :
                                   key.toLowerCase().includes('weight') || key.toLowerCase().includes('height') ? <Scale className="w-4 h-4 text-base-content/80" /> :
                                   <Activity className="w-4 h-4 text-base-content/80" />}
                                  <div className="text-xs text-base-content/80 uppercase tracking-wide font-semibold">{key.replace(/([A-Z])/g, ' $1').trim()}</div>
                                </div>
                                <div className="text-base-content font-medium">{String(value)}</div>
                              </div>
                            </div>
                          )
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {wellWomanData.structuredExtraction.symptomsAndConcerns && (
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">Symptoms and Concerns</h4>
                      <div className="space-y-4">
                        {wellWomanData.structuredExtraction.symptomsAndConcerns.chiefComplaint && (
                          <div className="card bg-base-100 shadow-sm border border-base-300">
                            <div className="card-body p-4">
                              <div className="flex items-center gap-2 mb-2">
                                <Stethoscope className="w-5 h-5 text-base-content" />
                                <h4 className="card-title text-base">Chief Complaint</h4>
                              </div>
                              <p className="text-base-content">{wellWomanData.structuredExtraction.symptomsAndConcerns.chiefComplaint}</p>
                            </div>
                          </div>
                        )}
                        {wellWomanData.structuredExtraction.symptomsAndConcerns.symptomList && wellWomanData.structuredExtraction.symptomsAndConcerns.symptomList.length > 0 && (
                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                            {wellWomanData.structuredExtraction.symptomsAndConcerns.symptomList.map((symptom, index) => (
                              <div key={index} className={`card shadow-sm border ${symptom.present ? 'border-error' : 'border-base-300'} bg-base-100`}>
                                <div className="card-body p-3">
                                  <div className="flex items-center gap-2 mb-1">
                                    <Stethoscope className={`w-4 h-4 ${symptom.present ? 'text-error' : 'text-base-content/80'}`} />
                                    <div className={`text-xs uppercase tracking-wide font-semibold ${symptom.present ? 'text-error' : 'text-base-content/80'}`}>{symptom.name}</div>
                                  </div>
                                  <div className={`font-medium ${symptom.present ? 'text-error' : 'text-base-content/70'}`}>
                                    {symptom.present ? 'Present' : 'Absent'}
                                  </div>
                                  {symptom.severity && (
                                    <div className={`text-sm mt-1 ${symptom.present ? 'text-error' : 'text-base-content/60'}`}>
                                      Severity: {symptom.severity}
                                    </div>
                                  )}
                                  {symptom.onsetOrPattern && (
                                    <div className={`text-sm mt-1 ${symptom.present ? 'text-error' : 'text-base-content/60'}`}>
                                      Onset/Pattern: {symptom.onsetOrPattern}
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  </div>
                </div>
              )}
            </div>
          )}


        </div>
      </div>

      {/* Edit & Send Summary Modal */}
      {isSendModalOpen && (
        <dialog className="modal modal-open">
          <div className="modal-box max-w-3xl">
            <h3 className="font-bold text-lg mb-4">Edit & Send Summary to Patient</h3>
            <div className="space-y-4">
              <div className="form-control">
                <label className="label">
                  <span className="label-text font-medium">Patient Summary</span>
                </label>
                <textarea
                  value={editedSummary}
                  onChange={(e) => setEditedSummary(e.target.value)}
                  className="textarea textarea-bordered w-full min-h-[300px] font-mono text-sm"
                  placeholder="Edit patient summary..."
                  disabled={sending}
                />
              </div>

              <div className="form-control">
                <label className="label">
                  <span className="label-text font-medium">Email address</span>
                </label>
                <input
                  type="email"
                  placeholder="name@example.com"
                  className="input input-bordered w-full"
                  value={emailAddress}
                  onChange={(e) => setEmailAddress(e.target.value)}
                  disabled={sending}
                />
              </div>
              
              <div className="form-control">
                <label className="label cursor-pointer justify-start gap-2">
                  <input
                    type="checkbox"
                    className="checkbox checkbox-sm"
                    checked={saveToPatient}
                    onChange={(e) => setSaveToPatient(e.target.checked)}
                    disabled={sending}
                  />
                  <span className="label-text">Save email to patient profile</span>
                </label>
              </div>
            </div>
            {showSuccess && (
              <div className="text-center text-success text-sm mt-4 mb-2">
                Patient summary sent successfully!
              </div>
            )}
            <div className="modal-action mt-6">
              <button
                className="btn btn-ghost"
                onClick={handleCloseSendModal}
                disabled={sending}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={() => submitSend(editedSummary)}
                disabled={sending || !emailAddress.trim() || !editedSummary.trim()}
              >
                {sending ? 'Sending...' : 'Send Email'}
              </button>
            </div>
          </div>
          <form method="dialog" className="modal-backdrop" onClick={handleCloseSendModal}>
            <button>close</button>
          </form>
        </dialog>
      )}
    </>
    );
};

